// main.js: Starlex v5 "GRAVITY". One scroll, five worlds, four cuts.
//
//   loader -> gate (press and hold: the cloud collapses, a star ignites)
//   -> 01 IGNITE (nebula) -[flash]-> 02 HOME (earth) -[zoom]-> 03 SCALE (galaxy)
//   -[lens]-> 04 GRAVITY (black hole) -[horizon]-> 05 YOURS (brand nebula)
//   -> 06 OFFER (a normal page over the blurred brand nebula)
//
// The film is a pure function of the smoothed scroll position (film.js).
// Worlds are loaded and warmed up ahead of time (worlds.js), cuts are
// composited from two HDR frames (transition.js), and the whole image goes
// through the shared film post pipeline (../post.js) with blended grades.
import * as THREE from 'three';
import Lenis from 'lenis';
import { Post } from './post.js';
import { detectTier, TIERS, Governor } from '../quality.js';
import { Score } from '../sound5.js';
import { Worlds, SLOTS } from './worlds.js';
import { Compositor, MODE, cutNeeds } from './transition.js';
import { Film, CHAPTERS, FILM_CHAPTERS, setPace } from './film.js';
import { Copy, Hud, Labels, splitWords } from './copy.js';
import { Gate } from './gate.js';
import { Pointer, SafeScore } from './input.js';
import { makePoster, download, designation } from './poster.js';
import { whereAmI, fmtLatLon } from './place.js';
import { makeGrade, mixGrade, towardGrade } from './grade.js';
import { $, $$, clamp, lerp, smooth, damp, hasWebGL2, store } from './util.js';
import './offerfx.js'; // DOM-only offer effects, owned by the art-direction agent (LIGHT.md)
import { GravityType } from './gravitytype.js'; // [gravity] pointer mass on the DOM text (gravity agent, LIGHT.md 4)
import { Stages } from './stages.js'; // [stages] v6 Zero-style gesture stages (shell agent)

const Q = new URLSearchParams(location.search);
const REDUCED = Q.has('reduced') || (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
const CH_SLOT = ['ignite', 'earth', 'galaxy', 'blackhole', 'brand', 'brand'];
const CUT_MODE = [MODE.flash, MODE.zoom, MODE.lens, MODE.horizon];
const LOAD_ORDER = ['ignite', 'earth', 'galaxy', 'blackhole', 'brand'];
// sign of each world's params.shiftY: +1 = positive moves the subject UP, -1 = CSS-like (down)
const SHIFT_UP = { ignite: -1, brand: -1, earth: 1, galaxy: 1, blackhole: 1 };
// DPR ceiling per tier for the shell (the governor can go lower)
const SHELL_CAP = { high: 1.5, mid: 1.5, low: 1.1 };
// where each world's subject is at the start / end of its chapter (anchor names to try)
const FOCUS = {
  ignite: { start: ['star', 'protostar', 'core', 'center'], end: ['star', 'protostar', 'core', 'center'] },
  earth: { start: ['sun', 'sunrise', 'limb'], end: ['dot', 'paleBlueDot', 'earth', 'planet', 'center'] },
  galaxy: { start: ['sun', 'you', 'here'], end: ['core', 'center', 'bulge', 'sgrA'] },
  blackhole: { start: ['center', 'hole', 'bh', 'shadow'], end: ['center', 'hole', 'bh', 'shadow'] },
  brand: { start: ['star', 'brand', 'core', 'center'], end: ['star', 'brand', 'core', 'center'] },
};
const log = (...a) => { if (Q.has('debug')) console.log('[v5]', ...a); window.__v5log && window.__v5log.push(a.join(' ')); };
window.__v5log = [];

function staticFallback(err) {
  console.warn('[v5] static fallback:', err && (err.message || err));
  try { splitWords(); } catch (e) { /* ignore */ }
  document.body.classList.add('no-gl');
  document.body.classList.remove('is-loading', 'is-gated');
  $$('.beat, .gate, .offer').forEach((b) => { b.classList.add('on', 'in'); b.inert = false; }); // [O3] copy.js marks hidden beats inert
  window.__v5 = { ready: true, fallback: true, error: String(err && (err.message || err)) };
}

if (!hasWebGL2() || Q.has('nogl')) staticFallback(new Error('WebGL2 unavailable'));
else boot().catch((e) => { console.error('[v5] boot failed', e); staticFallback(e); });

async function boot() {
  const body = document.body;
  const canvas = $('#gl');
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, stencil: false, depth: true, powerPreference: 'high-performance' });
  renderer.setClearColor(0x000000, 1);
  const { tier, gpu, mobile } = detectTier(renderer);
  const cfg = TIERS[tier];
  const post = new Post(renderer, { levels: cfg.bloom, msaa: 0 });
  const comp = new Compositor(renderer, { half: post.half });
  const worlds = new Worlds(renderer, { tier, half: post.half, mobile, cfg, base: '' }, log);
  const place = whereAmI(Q);
  $$('[data-city]').forEach((el) => { el.textContent = place.name; });
  $$('[data-coords]').forEach((el) => { el.textContent = fmtLatLon(place.lat, place.lon); });

  /* ---------- state ---------- */
  const S = {
    time: +(Q.get('t') || 0), clock: 0, dt: 0, frames: 0, rendered: 0, scale: 1, vw: innerWidth, vh: innerHeight, phone: innerWidth <= 760,
    ys: 0, yRaw: 0, vel: 0, lastCut: -1, lastCutT: 0, lastCh: 0, kick: 0, hint: false, errors: 0, brand: 'Starlex', brandT: -1,
    lit: false, revealed: false, focusA: [0.5, 0.5], focusB: [0.5, 0.5], offerIn: false, grab: null, ft: [], hidden: false, lost: false,
  };
  const F = {}; // film.at() output
  // v5.8 pace: the film is 1.8x longer on desktop and 2x on phones (fixed at load, so a rotation never
  // changes it). Chapter progress, beats and cut windows are unchanged; only the scroll distance grows.
  setPace(Q.get('pace') ? +Q.get('pace') : mobile ? 2.0 : 1.8);
  const film = new Film({ half: REDUCED ? [0.3, 0.3, 0.3, 0.3] : [0.5, 0.5, 0.42, 0.42] });
  const pointer = new Pointer();
  const forcedLook = Q.get('look') ? Q.get('look').split(',').map(Number) : null;
  const still = Q.has('still');

  // one input object per slot (params persist; progress/velocity per slot)
  const inputs = {};
  for (const k of Object.keys(SLOTS)) {
    inputs[k] = { progress: 0, velocity: 0, aspect: 1, hold: 0, pointer: pointer.p, look: pointer.look, params: { shiftX: 0, shiftY: 0, ...SLOTS[k].params }, _p: 0 };
  }
  Object.assign(inputs.earth.params, { lat: place.lat, lon: place.lon, city: place.name });
  if (Q.get('brand')) S.brand = Q.get('brand').slice(0, 28);
  inputs.brand.params.brand = S.brand;

  /* ---------- sound ---------- */
  let soundOn = !Q.has('mute') && store.get('v5-sound', 'on') !== 'off';
  const score = new SafeScore(Score, { muted: !soundOn });
  const soundBtn = $('#sound');
  let unlocked = false;
  function gesture() {
    if (unlocked) return;
    unlocked = true;
    score.unlock();
    score.setMuted(!soundOn);
  }
  function setSound(on) {
    soundOn = on;
    store.set('v5-sound', on ? 'on' : 'off');
    soundBtn.setAttribute('aria-pressed', String(on));
    score.setMuted(!on);
    if (on) gesture();
  }
  soundBtn.setAttribute('aria-pressed', String(soundOn));
  soundBtn.addEventListener('click', (e) => { e.stopPropagation(); gesture(); setSound(!soundOn); if (soundOn) score.cue('click'); });
  // iOS unlocks audio most reliably on touchend / click
  addEventListener('touchend', gesture, { passive: true });
  addEventListener('click', gesture);
  addEventListener('keydown', gesture);
  document.addEventListener('pointerover', (e) => { const t = e.target.closest && e.target.closest('a, button'); if (t && e.pointerType === 'mouse' && S.lit) score.cue('hover'); });
  document.addEventListener('click', (e) => { if (e.target.closest && e.target.closest('a, button') && S.lit) score.cue('click'); });

  /* ---------- sizes, quality governor ---------- */
  const probe = $('#vh-probe');
  const maxDpr = Q.get('dpr') ? +Q.get('dpr') : Math.min(window.devicePixelRatio || 1, SHELL_CAP[tier] || 1.5);
  const gov = new Governor(cfg, maxDpr, (s) => { S.scale = s; applySize(); log('governor ->', s); });
  if (Q.get('scale')) { gov.scale = +Q.get('scale'); gov.ceil = gov.scale; gov.min = gov.scale; }
  S.scale = gov.scale;
  let lastW = 0, lastH = 0;
  function applySize() {
    const w = Math.max(1, canvas.clientWidth || innerWidth), h = Math.max(1, canvas.clientHeight || innerHeight);
    S.vw = w; S.vh = h; S.phone = w <= 760;
    renderer.setPixelRatio(S.scale);
    renderer.setSize(w, h, false);
    const bw = Math.max(1, Math.floor(w * S.scale)), bh = Math.max(1, Math.floor(h * S.scale));
    post.setSize(bw, bh);
    comp.setSize(bw, bh);
    worlds.setSize(bw, bh);
    for (const k in inputs) inputs[k].aspect = w / h;
  }
  const offerEl = $('#offer'), trackEl = $('#track'), creditEl = $('#img-credit');
  let hud = null;
  function layout() {
    const lvh = probe.offsetHeight || innerHeight;
    film.layout(lvh, offerEl.offsetHeight || lvh);
    trackEl.style.height = `${film.trackPx}px`;
    if (hud) hud.layout(film);
  }
  let keepFilm = null; // set once Lenis exists (below)
  function resize(force) {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!force && w === lastW && Math.abs(h - lastH) < 2) return; // iOS toolbars: canvas is 100lvh, nothing to do
    lastW = w; lastH = h;
    const vh0 = film.vh, oh0 = film.offerH, y0 = S.ys;
    applySize();
    layout();
    // the track is measured in screens: keep the same chapter + progress (or the same point of the offer), or a rotation /
    // window resize lands somewhere else (390x844 -> 844x390 at Scale p 0.55 opened the offer)
    if (keepFilm && y0 > 0 && Math.abs(film.vh - vh0) > 1) {
      const end0 = (film.total + 1) * vh0;
      keepFilm(y0 <= end0 ? (y0 / vh0) * film.vh : film.yFor(5, clamp((y0 - end0) / Math.max(1, oh0 - vh0), 0, 1)));
    }
  }
  let rsT = 0;
  addEventListener('resize', () => { clearTimeout(rsT); rsT = setTimeout(() => resize(false), 140); });
  addEventListener('orientationchange', () => setTimeout(() => resize(true), 300));
  resize(true);

  /* ---------- context loss ---------- */
  canvas.addEventListener('webglcontextlost', (e) => {
    e.preventDefault();
    S.lost = true;
    body.classList.add('gl-lost');
    setTimeout(() => location.reload(), 2500);
  });
  canvas.addEventListener('webglcontextrestored', () => location.reload());

  /* ---------- scroll ---------- */
  // v5.8 speed limit for wheels and trackpads: one hard flick can no longer fling the visitor through a
  // whole chapter. Each event is capped (0.09 screens) and so is the distance per 120 ms (0.22 screens,
  // about 1.8 screens/s). Lenis reads deltaY after this callback, so editing it here is the supported hook.
  // ?nocap turns it off for A/B.
  const CAP = !Q.has('nocap');
  let capT = 0, capSum = 0;
  let stages = null; // [stages] set below once Lenis exists (its callback may fire before)
  const capWheel = (d) => {
    const e = d && d.event;
    if (!CAP || !e || e.type !== 'wheel') {
      if (stages) stages.onScroll(d); // [stages] touch deltas count toward a stage's push-through
      // [stages] a stage stops Lenis mid-swipe; Lenis would then preventDefault() the swipe's non-cancelable touchmoves, which
      // Chromium logs as errors ("Ignored attempt to cancel a touchmove event with cancelable=false"). They can't be cancelled anyway.
      if (e && e.cancelable === false && lenis.isStopped) return false;
      return true;
    }
    const now = performance.now();
    if (now - capT > 120) { capT = now; capSum = 0; }
    const per = film.vh * 0.09, win = film.vh * 0.22;
    let dy = Math.max(-per, Math.min(per, d.deltaY || 0));
    dy = Math.sign(dy) * Math.min(Math.abs(dy), Math.max(0, win - Math.abs(capSum)));
    capSum += dy;
    d.deltaY = dy;
    if (d.deltaX) d.deltaX = Math.max(-per, Math.min(per, d.deltaX));
    if (stages) stages.onScroll(d); // [stages] capped wheel deltas count toward the push-through, so one flick can't skip a stage
    return true;
  };
  const lenis = new Lenis({ lerp: REDUCED ? 0.2 : 0.07, smoothWheel: true, syncTouch: false, wheelMultiplier: 0.8, touchMultiplier: 1, virtualScroll: capWheel });
  lenis.stop();
  const scrollTo = (y, o = {}) => lenis.scrollTo(y, { force: true, ...o });
  keepFilm = (y) => { try { lenis.resize(); } catch (e) { /* Lenis re-measures on its own */ } y = Math.round(y); scrollTo(y, { immediate: true }); S.ys = y; };
  // [stages] v6 Zero-style stages (stages.js): the film holds at a chapter's stage point until a gesture (or ~1.2 screens of
  // scrolling, or Space/Enter) passes it; the reward animates that slot's params.stage 0 -> 1. ?nostages turns them off.
  if (!Q.has('nostages')) {
    try {
      stages = new Stages({
        film, lenis, reduced: REDUCED, cue: (n) => score.cue(n),
        slotFor: (ch) => CH_SLOT[ch], params: (slot) => inputs[slot] && inputs[slot].params, ready: (slot) => worlds.ready(slot),
        anchor: (slot, names) => { const w = worlds.get(slot), an = w && w.anchors; if (an) for (const n of names || []) { const a = an[n]; if (a && a.visible !== false && Number.isFinite(a.x) && Number.isFinite(a.y)) return [a.x * S.vw, a.y * S.vh]; } return null; },
      });
    } catch (e) { stages = null; console.warn('[v5] stages off:', e); }
  }
  // long jumps (links) cut through black instead of flying through every world
  const cover = $('#cover');
  function cutTo(y) {
    if (stages) stages.jumped(); // [stages] a link jump releases a held stage
    if (Math.abs(y - S.ys) < film.vh * 1.6) { scrollTo(y, { duration: 1.6 }); return; }
    cover.classList.add('on');
    setTimeout(() => { scrollTo(y, { immediate: true }); S.ys = y; setTimeout(() => cover.classList.remove('on'), 120); }, REDUCED ? 60 : 400);
  }
  $('#work-link').addEventListener('click', (e) => { e.preventDefault(); if (!S.lit) gate.forceLit(); cutTo(film.yFor(5, 0)); });
  $('#brand-link').addEventListener('click', (e) => { e.preventDefault(); if (S.lit) cutTo(0); });

  /* ---------- copy, HUD, labels ---------- */
  const copy = new Copy();
  hud = new Hud(film);
  layout();
  const labels = new Labels($('#labels'));
  const cityLabel = labels.add({ slot: 'earth', ch: 1, p0: 0.3, p1: 0.72, anchors: ['city', 'you', 'home'], text: place.name, sub: fmtLatLon(place.lat, place.lon) });
  // a thin ring + hairline so people find the pale blue dot (no claim, just a pointer)
  labels.add({ slot: 'earth', ch: 1, p0: 0.855, p1: 1.01, anchors: ['dot', 'paleBlueDot'], cls: 'dot', text: 'Earth' });
  labels.add({ slot: 'galaxy', ch: 2, p0: 0.1, p1: 0.46, anchors: ['sun', 'you', 'here'], text: 'You are here', sub: 'The Sun · Orion Arm' });
  labels.add({ slot: 'blackhole', ch: 3, p0: 0.12, p1: 0.4, anchors: ['photonRing', 'photon_ring', 'ring'], text: 'Photon ring', sub: 'light bent into orbit' });
  const brandLabel = labels.add({ slot: 'brand', ch: 4, p0: 0.2, p1: 1.01, anchors: ['star', 'brand', 'core'], cls: 'brand', text: S.brand.toUpperCase(), sub: designation(S.brand).code + ' · generated' });
  void cityLabel;

  /* ---------- the gate ---------- */
  const gate = new Gate({
    reduced: REDUCED, pointer,
    home: () => { const w = worlds.get('ignite'); const a = w && w.anchors && w.anchors.star; return a && a.visible !== false && Number.isFinite(a.x) ? [a.x * S.vw, a.y * S.vh] : null; },
    onGesture: (e) => { gesture(); if (e && e.type === 'pointerdown' && e.pointerType !== 'mouse') pointer.askGyro(); },
    onIgnite: (silent) => {
      S.lit = true;
      body.classList.add('is-lit');
      body.classList.remove('is-gated');
      lenis.start();
      if (!silent) {
        score.cue('ignite');
        if (!REDUCED) S.kick = 1;
        // ease into the first beat unless the visitor scrolls first
        S.autoY = film.yFor(0, 0.19);
        setTimeout(() => { if (S.ys < film.vh * 0.05) scrollTo(S.autoY, { duration: REDUCED ? 0.8 : 3.2, easing: (x) => 1 - Math.pow(1 - x, 4) }); }, REDUCED ? 150 : 90);
        setTimeout(() => { if (S.ys < S.autoY + film.vh * 0.12) { S.hint = true; body.classList.add('hint'); } }, 4300);
      }
    },
  });

  /* ---------- brand + poster ---------- */
  const form = $('#brand-form'), brandInput = $('#brand-input'), brandOut = $('#brand-out'), posterBtn = $('#poster-btn');
  function setBrand(name, cue = true) {
    name = (name || '').replace(/\s+/g, ' ').trim().slice(0, 28);
    if (!name) return;
    const same = name === S.brand;
    S.brand = name;
    S.brandT = S.clock;
    // on screen: the old star is pulled in and the new one is born from the same point
    if (!same && S.lit && F.filmCh === 4 && F.cut < 0 && F.offer < 0.05 && worlds.ready('brand') && !REDUCED) { S.reb = { name, t0: -1, cued: !cue }; cue = false; }
    else inputs.brand.params.brand = name;
    labels.setText(brandLabel, name.toUpperCase(), designation(name).code + ' · generated');
    brandOut.hidden = false;
    if (cue) score.cue('brand');
    try { history.replaceState(null, '', `?brand=${encodeURIComponent(name)}`); } catch (e) { /* file:// */ }
  }
  form.addEventListener('submit', (e) => { e.preventDefault(); setBrand(brandInput.value); brandInput.blur(); });
  if (Q.get('brand')) { brandInput.value = S.brand; brandOut.hidden = false; }
  const grabFrame = () => new Promise((res) => { S.grab = res; setTimeout(() => { if (S.grab === res) { S.grab = null; res(null); } }, 1500); });
  async function savePoster() {
    posterBtn.classList.add('busy');
    try {
      const c = await makePoster({ name: S.brand, world: worlds.get('brand'), renderSnapshot: grabFrame });
      const name = `${S.brand.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'brand'}-star-starlex.png`;
      window.__v5.lastPoster = { w: c.width, h: c.height, name };
      await download(c, name);
      score.cue('poster');
    } catch (e) { console.error('[v5] poster failed', e); }
    posterBtn.classList.remove('busy');
  }
  posterBtn.addEventListener('click', savePoster);

  /* ---------- GPU timer (?gpu) ---------- */
  const gl = renderer.getContext();
  const TQ = Q.has('gpu') ? gl.getExtension('EXT_disjoint_timer_query_webgl2') : null;
  const tqPend = []; let tqOpen = null;
  S.gpuMs = 0;
  function gpuBegin() { if (!TQ || tqOpen) return; tqOpen = gl.createQuery(); gl.beginQuery(TQ.TIME_ELAPSED_EXT, tqOpen); }
  function gpuEnd() {
    if (!tqOpen) return;
    gl.endQuery(TQ.TIME_ELAPSED_EXT); tqPend.push(tqOpen); tqOpen = null;
    while (tqPend.length && gl.getQueryParameter(tqPend[0], gl.QUERY_RESULT_AVAILABLE)) {
      const q = tqPend.shift();
      const ms = gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6;
      gl.deleteQuery(q);
      if (!gl.getParameter(TQ.GPU_DISJOINT_EXT) && ms > 0 && ms < 500) S.gpuMs = S.gpuMs ? S.gpuMs * 0.85 + ms * 0.15 : ms;
    }
    while (tqPend.length > 6) gl.deleteQuery(tqPend.shift());
  }

  /* ---------- the loop ---------- */
  const grade = makeGrade();
  const OFFER_GRADE = { exposure: 0.34, sat: 0.85, vignette: 0.75, bloomK: 0.35 };
  const scrim = $('#scrim');
  const waitEl = $('#wait'), waitTxt = $('#wait-txt');
  const WORLD_NAME = { earth: 'Earth', galaxy: 'the Milky Way', blackhole: 'Sagittarius A*', brand: 'your star', ignite: 'the nebula' };
  const ring3 = (a) => [a[0], a[1]];
  function anchorFocus(slot, which, fallback) {
    const w = worlds.get(slot);
    const names = FOCUS[slot] && FOCUS[slot][which];
    if (w && w.anchors && names) {
      for (const n of names) {
        const a = w.anchors[n];
        if (a && a.visible !== false && Number.isFinite(a.x) && Number.isFinite(a.y)) return [clamp(a.x, 0.05, 0.95), clamp(1 - a.y, 0.05, 0.95)];
      }
    }
    return fallback;
  }
  function driveSlot(slot, progress, dt) {
    const inp = inputs[slot];
    const p = clamp(progress, 0, 1);
    inp.velocity = dt > 0 ? (p - inp._p) / dt : 0;
    inp._p = p;
    inp.progress = p;
    return worlds.update(slot, dt, S.time, inp);
  }
  // move the world's subject aside for the copy; k fades it (0 at the heart of a cut)
  function setShift(slot, ch, p, k = 1) {
    const sh = copy.shift(ch, p, S.phone);
    const par = inputs[slot].params;
    const up = SHIFT_UP[slot] || 1;
    if (S.phone) { par.shiftX = 0; par.shiftY = up * 0.08 * sh.y * k; } else { par.shiftX = 0.18 * sh.x * k; par.shiftY = up * 0.05 * sh.y * k; }
    return sh;
  }
  // nearest ready slot at or before chapter ch (to hold on while a world is still loading)
  function heldSlot(ch) {
    for (let i = ch; i >= 0; i--) if (worlds.ready(CH_SLOT[i])) return { slot: CH_SLOT[i], p: i === ch ? null : 0.84, ch: i };
    return null;
  }

  let lastNow = 0;
  function frame(now) {
    requestAnimationFrame(frame);
    if (S.hidden || S.lost) { lastNow = 0; return; }
    const rawDt = lastNow ? (now - lastNow) / 1000 : 1 / 60;
    lastNow = now;
    const dt = Math.min(rawDt, 0.05);
    S.dt = dt;
    S.clock += dt;
    if (!still) S.time += dt;
    S.ft.push(rawDt * 1000); if (S.ft.length > 120) S.ft.shift();
    S.frames++;
    const c0 = performance.now();
    try { tick(now, dt, rawDt); } catch (e) {
      S.errors++;
      if (S.errors < 5) console.error('[v5] frame error', e);
    }
    const cms = performance.now() - c0;
    S.cpuMs = S.cpuMs ? S.cpuMs * 0.9 + cms * 0.1 : cms;
  }

  function tick(now, dt, rawDt) {
    lenis.raf(now);
    const y = lenis.animatedScroll !== undefined ? lenis.animatedScroll : scrollY;
    S.yRaw = y;
    // a second, light smoothing layer for native touch scrolling; big jumps snap
    if (Math.abs(y - S.ys) > film.vh * 2.5) S.ys = y; else S.ys = damp(S.ys, y, REDUCED ? 30 : 14, dt);
    if (Math.abs(S.ys - y) < 0.5) S.ys = y;
    const prevU = F.u || 0;
    film.at(S.ys, F);
    S.vel = dt > 0 ? (F.u - prevU) / dt : 0;
    gate.tick(Math.min(rawDt, 0.2)); // real time: slow devices must not make the hold longer
    pointer.tick(dt, forcedLook);
    if (stages) { try { stages.tick(Math.min(rawDt, 0.2), S); } catch (e) { console.warn('[v5] stages off:', e); try { stages.dispose(); } catch (x) { /* ignore */ } stages = null; } } // [stages] hold, gesture, reward (real time)

    // loading: the first world (the loader covers the screen until it is warm)
    const warming = worlds.pending();
    if (!S.revealed) {
      if (worlds.ready('ignite')) reveal();
    }
    // keep loading ahead: next world in film order, nearest to where we are first
    scheduleLoads();

    const ch = F.filmCh;
    const inCut = F.cut >= 0;
    let mode = -1, A = null, B = null, pA = 0, pB = 0, cutT = 0;
    if (inCut) {
      A = CH_SLOT[F.cut]; B = CH_SLOT[F.cut + 1];
      pA = film.progressOf(F.cut, F.u); pB = film.progressOf(F.cut + 1, F.u);
      cutT = F.t;
      mode = CUT_MODE[F.cut];
      if (F.cut === 0) {
        // both worlds have their own white ends (nebula p->1, earth p->0): stop short of them,
        // so the white is only the transition's brief peak
        const sp = film.cutSpan(0); const e = Math.min(1, cutT * 2); pA = sp.outP + (0.87 - sp.outP) * e * e;
        const x = clamp((cutT - 0.5) / 0.5, 0, 1); pB = 0.045 + (Math.max(0.05, sp.inP) - 0.045) * (1 - Math.pow(1 - x, 2));
      }
      if (!worlds.ready(B)) { B = null; }
      if (!worlds.ready(A)) { A = B; pA = pB; B = null; }
    } else {
      A = CH_SLOT[ch]; pA = F.p;
      if (!worlds.ready(A)) { const h = heldSlot(ch); if (h) { A = h.slot; pA = h.p === null ? F.p : h.p; } else A = null; }
    }

    S.waiting = S.lit && ((!inCut && A !== CH_SLOT[ch]) || (inCut && cutT > 0.5 && !worlds.ready(CH_SLOT[F.cut + 1])));
    S.waitFor = S.waiting ? (inCut ? CH_SLOT[F.cut + 1] : CH_SLOT[ch]) : '';
    // world inputs
    const hold = S.lit ? 1 : gate.hold;
    inputs.ignite.hold = hold;
    inputs.brand.hold = 1;
    inputs.ignite.params.igniteT = gate.igniteT;
    inputs.brand.params.brandT = S.brandT < 0 ? -1 : S.clock - S.brandT;
    inputs.brand.params.backdrop = F.offer > 0.7 ? 1 : 0; // the nebula's cheap mode behind the blurred offer
    if (A) { if (!inCut) { setShift(A, ch, pA); if (A === 'brand' && F.offer > 0) { const k = smooth(0, 1, F.offer); inputs.brand.params.shiftX *= 1 - k; inputs.brand.params.shiftY = lerp(inputs.brand.params.shiftY, S.phone ? -0.36 : -0.42, k); } } else setShift(A, F.cut, pA, 1 - smooth(0.0, 0.45, cutT)); driveSlot(A, pA, dt); }
    if (B) { setShift(B, F.cut + 1, pB, smooth(0.55, 1.0, cutT)); driveSlot(B, pB, dt); }

    // grade
    const wa = A && worlds.get(A), wb = B && worlds.get(B);
    const ga = wa && wa.grade, gb = wb && wb.grade;
    if (B) mixGrade(grade, ga, gb, smooth(0.35, 0.65, cutT)); else mixGrade(grade, ga, ga, 0);
    const offerK = F.offer;
    if (offerK > 0) towardGrade(grade, OFFER_GRADE, smooth(0, 1, offerK));
    { const g0 = mode === MODE.lens ? lensGrade(grade, cutT) : grade; post.setGrade(stages ? stages.grade(g0) : g0); } // [stages] a reward swells the grade
    post.u.time.value = S.time;
    // [gravity] the pointer has mass: lens uniforms for the final pass (gravity agent, LIGHT.md 4).
    // Off before ignition (and the hold gate), in the offer and for reduced motion; eased out mid-cut.
    {
      const Lz = pointer.lensTick(rawDt, S.lit && !REDUCED && F.offer < 0.3);
      const cutK = inCut ? 1 - smooth(0, 0.18, Math.min(cutT, 1 - cutT)) : 1;
      S.lensK = S.grab ? 0 : Lz.e * cutK * (1 - smooth(0, 0.3, F.offer)); // a poster grab never keeps the lens
      post.setLens(Lz.x / Math.max(1, S.vw), 1 - Lz.y / Math.max(1, S.vh), (S.phone ? 48 : 70) / Math.max(1, S.vh), S.lensK);
      // [stages] a reward pulses a lens on the stage ring: the pointer's own mass, briefly much heavier
      const SL = stages && stages.fx;
      if (SL && SL.lens > 0.001) post.setLens(SL.x / Math.max(1, S.vw), 1 - SL.y / Math.max(1, S.vh), (S.phone ? 48 : 70) * (1 + 1.0 * SL.lens) / Math.max(1, S.vh), Math.max(S.lensK || 0, Math.min(1, SL.lens)));
    }

    // render (guard: a clear colour leaked by any module must not become every world's background)
    renderer.setClearColor(0x000000, 1);
    gpuBegin();
    if (A && S.revealed) {
      const dflt = S.phone ? [0.5, 0.56] : [0.5, 0.5];
      if (B) {
        S.focusA = anchorFocus(A, 'end', dflt);
        S.focusB = anchorFocus(B, 'start', dflt);
        const [tA, tB] = comp.targets();
        const [nA, nB0] = cutNeeds(mode, cutT);
        // LENS: the lens over the galaxy is sized by the black hole's own shadow (at its first frame, p = 0),
        // so it is measured once up front: no size jump when the black hole starts to render mid-cut
        const nB = nB0 || (mode === MODE.lens && cutT <= 0.5 && (!S.lensR || S.lensAsp !== S.vw / S.vh) && (S.lensTry || 0) <= 4);
        post.renderWith((rt) => {
          if (nA) worlds.render(A, tA);
          if (nB) worlds.render(B, tB);
          renderer.autoClear = true;
          comp.draw(rt, mode, cutT, { fA: S.focusA, fB: S.focusB, time: S.time, reduced: REDUCED, amount: mode === MODE.flash ? flashPulse(cutT) : mode === MODE.lens ? lensR(B, cutT <= 0.5) : 0 });
        });
      } else if (S.reb && A === 'brand') {
        const [, tB] = comp.targets();
        const FR = comp.freeze();
        if (S.reb.t0 < 0) {
          // freeze the old star, then hand the world its new name
          worlds.render('brand', FR);
          renderer.autoClear = true;
          S.reb.fA = anchorFocus('brand', 'start', dflt);
          inputs.brand.params.brand = S.reb.name;
          S.reb.t0 = S.clock;
        }
        const rt01 = clamp((S.clock - S.reb.t0) / 1.9, 0, 1);
        if (!S.reb.cued && rt01 > 0.42) { S.reb.cued = true; score.cue('brand'); }
        post.renderWith((rt) => {
          worlds.render('brand', tB);
          renderer.autoClear = true;
          comp.draw(rt, MODE.rebirth, rt01, { fA: S.reb.fA, fB: anchorFocus('brand', 'start', dflt), time: S.time, reduced: REDUCED, texA: FR.texture });
        });
        if (rt01 >= 1) S.reb = null;
      } else if (offerK > 0.001) {
        const [tA] = comp.targets();
        post.renderWith((rt) => {
          worlds.render(A, tA);
          renderer.autoClear = true;
          comp.draw(rt, MODE.backdrop, 0, { amount: smooth(0, 1, offerK), time: S.time });
        });
      } else if (A === 'ignite' && !S.lit && gate.hold > 0.002) {
        const [tA] = comp.targets();
        post.renderWith((rt) => {
          worlds.render(A, tA);
          renderer.autoClear = true;
          comp.draw(rt, MODE.pull, 0, { amount: gate.hold, fA: anchorFocus('ignite', 'start', dflt), time: S.time, reduced: REDUCED });
        });
      } else {
        post.renderWith((rt) => { worlds.render(A, rt); renderer.autoClear = true; });
      }
      S.rendered++;
      if (S.grab) { const g = S.grab; S.grab = null; try { const c = document.createElement('canvas'); c.width = canvas.width; c.height = canvas.height; c.getContext('2d').drawImage(canvas, 0, 0); g(c); } catch (e) { g(null); } }
    } else if (!S.revealed) {
      renderer.setRenderTarget(null); renderer.clear();
    }

    gpuEnd();
    // warm the next worlds up: one small render per frame, never during a cut or while the visitor holds
    if (warming && !inCut && !gate.holding && !gate.keyHold) worlds.warmStep(inputs);
    // dynamic resolution (not during cuts or warm-ups: those are brief, deliberate spikes)
    if (S.revealed && !inCut && !warming) gov.tick(rawDt);

    dom(ch, inCut, A, cutT);
    sound(dt, inCut, cutT);
    // [gravity] the same lens bends the headline letters; Sagittarius A* lenses chapter 3's text and
    // spaghettifies it at the horizon (gravitytype.js, gravity agent). Off for reduced motion.
    if (!REDUCED && S.gt !== false) {
      try {
        if (!S.gt) S.gt = new GravityType();
        S.gt.update({ S, F, inCut, cutT, lens: pointer.lens, k: S.lensK || 0, te: S.phone ? 48 : 70, anchor: window.__v5 && window.__v5.anchor });
      } catch (e) { S.gt = false; console.warn('[v5] gravity type off:', e); }
    }
  }

  function dom(ch, inCut, A, cutT) {
    // copy beats
    const yoursHidden = F.offer > 0.35;
    // the pale-blue-dot line (and its marker) holds into the rush at the dot, up to just before the swap
    // (cut 2 starts at HOME p 0.881; t < 0.42 = p < 0.98, the beat's data-out)
    const linger = inCut && F.cut === 1 && cutT < 0.42;
    const visible = S.lit && (!inCut || linger) && !S.waiting && !(ch === 4 && yoursHidden);
    if (S.waitFor !== S.waitShown) { waitEl.classList.toggle('on', !!S.waitFor); if (S.waitFor) waitTxt.textContent = `Loading ${WORLD_NAME[S.waitFor] || 'the next world'}`; S.waitShown = S.waitFor; }
    const any = copy.update(ch, F.p, visible);
    if (S.phone) { const on = any; if (on !== S.copyOn) { body.classList.toggle('copy-on', on); S.copyOn = on; } }
    // offer
    const offerIn = F.offer > 0.25;
    if (offerIn !== S.offerIn) { offerEl.classList.toggle('in', offerIn); S.offerIn = offerIn; }
    // HUD
    if (S.lit) hud.set(F.ch, F.global);
    // image credit wherever a credited image is on screen (ESO / NASA terms)
    const shown = inCut ? (cutT < 0.5 ? F.cut : F.cut + 1) : ch;
    const cr = S.lit && F.offer < 0.3 && CHAPTERS[shown] && CHAPTERS[shown].credit && worlds.ready(CH_SLOT[shown]) ? CHAPTERS[shown].credit : '';
    if (cr !== S.credit) { S.credit = cr; if (cr) creditEl.textContent = cr; creditEl.classList.toggle('on', !!cr); }
    // labels on world anchors
    labels.update({ slot: A, ch, p: F.p, world: A && (!inCut || linger) && F.offer < 0.05 && !S.waiting ? worlds.get(A) : null, w: S.vw, h: S.vh });
    const offerOn = F.offer > 0.5;
    if (offerOn !== S.offerHud) { body.classList.toggle('offer-on', offerOn); S.offerHud = offerOn; }
    if (F.ch !== S.dch) { body.dataset.ch = String(F.ch); S.dch = F.ch; } // CSS: body[data-ch] sets --acc (LIGHT.md)
    // scroll hint
    if (S.hint && S.ys > (S.autoY || 0) + film.vh * 0.22) { S.hint = false; body.classList.remove('hint'); }
    // ignition kick (DOM only; respects reduced motion)
    if (S.kick > 0.002) {
      S.kick *= Math.exp(-S.dt * 5.5);
      const a = S.kick * 7, t = S.clock * 60;
      canvas.style.transform = `translate3d(${(Math.sin(t * 1.3) * a).toFixed(2)}px, ${(Math.cos(t * 1.7) * a).toFixed(2)}px, 0) scale(${(1 + 0.012 * S.kick).toFixed(4)})`;
    } else if (S.kick > 0) { S.kick = 0; canvas.style.transform = ''; }
    void scrim;
  }

  // the flash cut peaks as a short pulse in TIME, fired when the scroll crosses the swap:
  // scrolling through flashes, but a frame at rest anywhere in the cut is never a white field
  function flashPulse(t) {
    if (!(S.flashT >= 0)) return 0;
    const age = S.clock - S.flashT;
    return age > 1.5 ? 0 : Math.exp(-age * 7) * Math.exp(-((t - 0.5) * (t - 0.5)) / 0.0144);
  }

  // the black hole's shadow radius on screen (screen-height units), from its anchors, for the LENS cut
  // (measured only while the black hole sits at p = 0, i.e. up to the swap; gives up after a few tries)
  function lensR(slot, measure) {
    const w = worlds.get(slot), an = w && w.anchors, c = an && an.center, e = an && an.photonRing;
    let ok = false;
    if (measure && c && e && e.visible !== false && Number.isFinite(c.x + c.y + e.x + e.y)) {
      const r = Math.hypot((e.x - c.x) * S.vw / Math.max(1, S.vh), e.y - c.y);
      // floor 0.003: portrait widens the vertical field ~3x, so the arrival shadow is only ~0.0055 of the
      // screen height on a phone (it was rejected by the old 0.015 floor and fell back to 0.11, a 20x jump)
      if (r > 0.003 && r < 0.45) { S.lensR = r; S.lensAsp = S.vw / S.vh; ok = true; }
    }
    // fallback mirrors blackhole/rig.js: desktop arrival shadow ~0.017, portrait scales by 1.44 / aspect
    const fb = 0.017 / Math.max(1, 1.44 / Math.max(0.2, S.vw / Math.max(1, S.vh)));
    if (measure && !ok && !S.lensR && worlds.ready(slot) && (S.lensTry = (S.lensTry || 0) + 1) > 4) S.lensR = fb;
    return S.lensR || fb;
  }

  // LENS cut: hold the bloom back around the swap, so the bright galaxy core cannot fill the shadow with glare
  const LG = {};
  function lensGrade(g, t) {
    if (!g) return g;
    Object.assign(LG, g);
    const k = Math.exp(-Math.pow((t - 0.42) / 0.16, 2));
    if (Number.isFinite(g.bloomK)) LG.bloomK = g.bloomK * (1 - 0.6 * k);
    return LG;
  }

  function sound(dt, inCut, cutT) {
    const tri = inCut ? 1 - Math.abs(cutT - 0.5) * 2 : 0;
    // cues at the heart of each cut
    if (inCut) {
      const c = F.cut;
      if (S.lastCut === c) {
        const crossed = (a) => (S.lastCutT < a && cutT >= a) || (S.lastCutT > a && cutT <= a);
        if (c === 3 && crossed(0.32)) score.cue('horizon');
        else if (c !== 3 && crossed(0.5)) { score.cue('whoosh'); if (c === 0 && !REDUCED) S.flashT = S.clock; }
      }
      S.lastCutT = cutT;
    }
    S.lastCut = inCut ? F.cut : -1;
    score.update(dt, {
      chapter: F.ch, progress: F.ch === 5 ? F.p5 : F.p, global: F.global, charge: S.lit ? 1 : gate.hold, velocity: S.vel,
      igniteT: gate.igniteT, brand: S.brand, transition: tri, look: pointer.look,
    });
    const live = score.running && soundOn;
    if (live !== S.soundLive) { soundBtn.classList.toggle('live', live); S.soundLive = live; }
  }

  function scheduleLoads() {
    if (!S.revealed || !S.lit) return;
    // the chapter we are in (or heading to) first, then the rest in film order
    const want = [];
    const here = Math.min(4, F.filmCh + (F.cut >= 0 ? 1 : 0));
    want.push(CH_SLOT[here], CH_SLOT[Math.min(4, here + 1)]);
    for (const s of LOAD_ORDER) if (!want.includes(s)) want.push(s);
    // one world loading at a time keeps the network and the main thread calm
    for (const s of want) {
      const e = worlds.entries[s];
      if (e && (e.status === 'loading' || e.status === 'loaded')) return;
    }
    for (const s of want) {
      const e = worlds.entries[s];
      if (!e || e.status === 'idle') { worlds.prepare([s]); log('loading', s); return; }
    }
  }

  function reveal() {
    S.revealed = true;
    const go = () => {
      body.classList.remove('is-loading');
      window.__v5.ready = true;
      const deep = Q.get('ch');
      if (Q.has('skip') || deep !== null || Q.get('brand')) {
        gate.forceLit();
        const y = deep !== null ? film.yFor(+deep, +(Q.get('p') || 0)) : 0;
        scrollTo(y, { immediate: true });
        S.ys = y;
      } else {
        setTimeout(() => gate.show(), REDUCED ? 100 : 700);
      }
    };
    const fonts = document.fonts && document.fonts.ready ? Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 2500))]) : Promise.resolve();
    fonts.then(() => requestAnimationFrame(() => requestAnimationFrame(go)));
  }

  /* ---------- visibility ---------- */
  document.addEventListener('visibilitychange', () => {
    S.hidden = document.hidden;
    score.setMuted(S.hidden || !soundOn);
    lastNow = 0;
  });

  /* ---------- go ---------- */
  comp.warm();
  worlds.prepare(['ignite']);
  requestAnimationFrame(frame);

  /* ---------- QA / debug surface ---------- */
  window.__v5 = {
    ready: false, S, F, film, lenis, gov, tier, gpu, mobile, place, renderer, post, gate,
    get fps() { const s = S.ft.slice(-60); return s.length < 5 ? 0 : 1000 / (s.reduce((a, b) => a + b, 0) / s.length); },
    get worst() { return S.ft.length ? Math.max(...S.ft.slice(-60)) : 0; },
    worlds: () => worlds.status(),
    state() {
      return {
        ch: F.ch, filmCh: F.filmCh, p: +(F.p || 0).toFixed(3), cut: F.cut, t: +(F.t || 0).toFixed(3), offer: +(F.offer || 0).toFixed(2),
        lit: S.lit, hold: +gate.hold.toFixed(2), fps: Math.round(this.fps), worst: Math.round(this.worst), scale: gov.scale, tier, errors: S.errors,
        y: Math.round(S.ys), brand: S.brand, gpu: +S.gpuMs.toFixed(2), cpu: +(S.cpuMs || 0).toFixed(2), waiting: S.waitFor || '',
      };
    },
    ignite() { gate.forceLit(); },
    jump(ch, p = 0) { if (!S.lit) gate.forceLit(); const y = film.yFor(ch, p); if (stages) stages.jumped(); scrollTo(y, { immediate: true }); S.ys = y; return y; }, // [stages] jumps release
    jumpCut(i, t = 0.5) { if (!S.lit) gate.forceLit(); const y = film.yForCut(i, t); if (stages) stages.jumped(); scrollTo(y, { immediate: true }); S.ys = y; return y; },
    yFor: (ch, p) => film.yFor(ch, p), yForCut: (i, t) => film.yForCut(i, t),
    scrollTo(y, immediate = false) { if (stages) stages.jumped(); scrollTo(y, { immediate }); },
    get stages() { return stages; }, // [stages] QA: state(), arm(i), ring()
    setBrand, savePoster,
    // read-only helpers for DOM layers (gravitytype.js, offerfx.js): a world's anchor in screen 0..1 from the top-left
    anchor(slot, n) { const w = worlds.get(slot), a = w && w.anchors && w.anchors[n]; return a ? { x: a.x, y: a.y, visible: a.visible !== false } : null; },
    slotFor: (ch) => CH_SLOT[ch],
    lastPoster: null,
  };
}
